import { Box, createStyles, Popover } from "@mantine/core";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";
import AppNavbar from "../../../components/AppNavbar/AppNavbar";
import ClaimToolbar from "../../../components/ClaimBilling/ClaimToolbar";
import TableLoader from "../../../components/TableLoader";
import RecordNotFound from "../../../components/RecordNotFound";
import { getAllBillingModal, GetBillingERADetais } from "../../../services";
import { getAllPatients } from "../../../services/patient";
import { decrypt } from "../../../utils/index";
import {
  LuArrowLeft,
  LuChevronLeft,
  LuChevronRight,
  LuDownload,
  LuX,
} from "react-icons/lu";

const INK        = "#171717";
const INK2       = "#262626";
const MUTED      = "#667085";
const LINE       = "#E5E5E5";
const LINE_SOFT  = "#F1F1F1";
const SURFACE_SOFT = "#FAFAFA";
const SURFACE_ALT  = "#F4F4F5";
const ACCENT     = "#FFBA45";
const ACCENT_HOVER = "#EEAA33";
const GREEN      = "#15803D";
const GREEN_SOFT = "#DCFCE7";
const GREEN_BORDER = "#86E1B0";

const useStyles = createStyles(() => ({
  backBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    height: 34,
    padding: "0 12px",
    borderRadius: 7,
    border: `1px solid ${LINE}`,
    background: "transparent",
    color: INK2,
    fontSize: 12.5,
    fontWeight: 600,
    cursor: "pointer",
    margin: "16px 0 12px",
    width: "fit-content",
    "& svg": { width: 15, height: 15 },
    "&:hover": { background: SURFACE_ALT },
  },

  /* ── grid ── */
  grid: {
    display: "grid",
    gridTemplateColumns: "360px minmax(0, 1fr)",
    gap: 16,
    alignItems: "start",
    position: "relative",
    transition: "grid-template-columns .25s ease, gap .25s ease, padding-left .25s ease",
  },
  gridCollapsed: {
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: 0,
    paddingLeft: 44,
  },
  listHidden: { display: "none" },

  /* ── collapse arrow ── */
  collapseArrow: {
    position: "absolute",
    top: 120,
    left: 360,
    transform: "translate(-50%, 0)",
    width: 26,
    height: 26,
    background: "#fff",
    border: `1px solid ${LINE}`,
    borderRadius: 999,
    cursor: "pointer",
    zIndex: 5,
    display: "grid",
    placeItems: "center",
    color: INK2,
    padding: 0,
    boxShadow: "0 2px 6px rgba(15,15,20,0.08)",
    transition: "left .25s ease, top .25s ease, width .25s ease, height .25s ease, border-radius .25s ease, background .12s ease",
    "& svg": { width: 14, height: 14 },
    "&:hover": { background: SURFACE_SOFT, borderColor: INK2 },
  },
  collapseArrowCollapsed: {
    left: 0,
    top: 64,
    transform: "translate(0, 0)",
    width: 36,
    height: "auto",
    minHeight: 150,
    borderRadius: 10,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    padding: "14px 0",
    background: INK,
    color: "#fff",
    borderColor: INK,
    boxShadow: "0 4px 12px rgba(15,15,20,0.18)",
    "& svg": { width: 16, height: 16, transform: "rotate(180deg)" },
    "&:hover": { background: "#1A1A1A", borderColor: "#1A1A1A" },
  },
  gcIcon: { display: "grid", placeItems: "center" },
  gcLabel: {
    writingMode: "vertical-rl",
    transform: "rotate(180deg)",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.09em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    color: "#fff",
  },

  /* ── left list ── */
  list: {
    background: "#fff",
    border: `1px solid ${LINE}`,
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
    position: "sticky",
    top: 16,
    height: "100vh",
    display: "flex",
    flexDirection: "column",
  },
  listHead: {
    padding: "12px 16px",
    borderBottom: `1px solid ${LINE}`,
    background: SURFACE_SOFT,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    "& h3": { margin: 0, fontSize: 14, fontWeight: 700, color: INK, letterSpacing: "0.01em" },
  },
  listScroll: { flex: 1, overflowY: "auto" },
  card: {
    display: "block",
    padding: "14px 16px",
    background: "#fff",
    border: 0,
    borderBottom: `1px solid ${LINE_SOFT}`,
    cursor: "pointer",
    transition: "background .12s ease",
    width: "100%",
    textAlign: "left",
    "&:last-child": { borderBottom: 0 },
    "&:hover": { background: SURFACE_SOFT },
  },
  cardHoverPaid:    { "&:hover": { background: "#F4FAF6" } },
  cardHoverPending: { "&:hover": { background: "#F2F7FD" } },
  cardHoverAck:     { "&:hover": { background: "#F5F3FF" } },
  cardSelected: {
    background: "#F0FDF4",
    boxShadow: "inset 3px 0 0 #B91C1C",
    "&:hover": { background: "#F0FDF4" },
  },
  cardSelectedPending: {
    background: "#EBF2FB",
    boxShadow: "inset 3px 0 0 #B91C1C",
    "&:hover": { background: "#EBF2FB" },
  },
  cardSelectedAck: {
    background: "#F5F3FF",
    boxShadow: "inset 3px 0 0 #B91C1C",
    "&:hover": { background: "#F5F3FF" },
  },
  cardRow1: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 },
  cardId: { fontSize: 12, fontWeight: 700, color: INK, letterSpacing: "0.02em" },
  cardAmt: { fontSize: 13, fontWeight: 700, color: INK },
  cardName: { margin: "6px 0 4px", fontSize: 13.5, fontWeight: 600, color: INK },
  cardPayer: { fontSize: 12, color: MUTED, lineHeight: 1.5 },
  cardProvider: { display: "block", color: MUTED, "& b": { color: INK } },
  cardInfo: {
    fontSize: 11.5,
    color: GREEN,
    background: "#F0FDF4",
    border: "1px solid #C6E2C9",
    borderRadius: 6,
    padding: "5px 8px",
    marginTop: 8,
    lineHeight: 1.45,
    fontWeight: 500,
    "& b": { color: "#14532D", fontWeight: 700 },
  },
  cardInfoPending: {
    fontSize: 11.5,
    color: "#2C5BA3",
    background: "#EFF6FF",
    border: "1px solid #BFD4F0",
    borderRadius: 6,
    padding: "5px 8px",
    marginTop: 8,
    lineHeight: 1.45,
    fontWeight: 500,
    "& b": { color: "#1E3A8A", fontWeight: 700 },
  },
  cardInfoAck: {
    fontSize: 11.5,
    color: "#6941C6",
    background: "#F9F5FF",
    border: "1px solid #D9D0FA",
    borderRadius: 6,
    padding: "5px 8px",
    marginTop: 8,
    lineHeight: 1.45,
    fontWeight: 500,
    "& b": { color: "#4C2D91", fontWeight: 700 },
  },

  /* ── right panel ── */
  pageId: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 14,
    "& h2": { margin: 0, fontSize: 20, fontWeight: 700, color: INK, display: "inline-flex", alignItems: "center", gap: 10 },
  },
  countBadge: {
    display: "inline-grid",
    placeItems: "center",
    minWidth: 32,
    height: 32,
    padding: "0 8px",
    borderRadius: 999,
    fontSize: 13.5,
    fontWeight: 800,
    background: GREEN_SOFT,
    color: GREEN,
    border: `1.5px solid ${GREEN_BORDER}`,
  },
  countBadgePending: { background: "#EFF6FF", color: "#2C5BA3", border: "1.5px solid #BFD4F0" },
  countBadgeAck:     { background: "#F9F5FF", color: "#6941C6", border: "1.5px solid #D9D0FA" },
  statusTrigger: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 12px 6px 10px",
    border: `1px solid ${LINE}`,
    borderRadius: 999,
    background: "#fff",
    fontSize: 12.5,
    fontWeight: 600,
    color: GREEN,
    marginBottom: 14,
    cursor: "pointer",
    "& svg": { width: 12, height: 12 },
    "&:hover": { borderColor: INK2, background: SURFACE_SOFT },
  },
  statusTriggerPending: { color: "#2C5BA3", borderColor: "#BFD4F0", "&:hover": { borderColor: "#2C5BA3" } },
  statusTriggerAck:     { color: "#6941C6", borderColor: "#D9D0FA", "&:hover": { borderColor: "#6941C6" } },
  statusDot: { width: 8, height: 8, borderRadius: 999, background: "currentColor", flexShrink: 0 },
  statusLabel: { color: MUTED, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", fontSize: 11 },

  /* ── cms form ── */
  cmsForm: {
    background: "#fff",
    border: `1px solid ${LINE}`,
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
  },
  cmsFormHead: {
    padding: "12px 18px",
    borderBottom: `1px solid ${LINE_SOFT}`,
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    alignItems: "center",
    gap: 14,
  },
  headPatient: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 999,
    background: "#FFF4DA",
    color: "#B7791F",
    fontSize: 13,
    fontWeight: 700,
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
    overflow: "hidden",
    backgroundSize: "cover",
    backgroundPosition: "center",
  },
  headInfo: { display: "flex", flexDirection: "column", minWidth: 0 },
  headName: { fontSize: 13, fontWeight: 700, color: INK },
  headMeta: { fontSize: 11.5, color: MUTED },
  headTitle: {
    margin: 0,
    fontSize: 14.5,
    fontWeight: 700,
    color: INK,
    justifySelf: "center",
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    whiteSpace: "nowrap",
  },
  claimIdMono: {
    fontFamily: "ui-monospace, Menlo, monospace",
    fontSize: 13,
    fontWeight: 600,
    color: INK2,
    padding: "2px 8px",
    background: SURFACE_SOFT,
    borderRadius: 5,
  },
  navArrow: {
    width: 26,
    height: 26,
    border: 0,
    background: "transparent",
    borderRadius: 6,
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
    color: INK2,
    "& svg": { width: 16, height: 16 },
    "&:hover": { background: SURFACE_SOFT, color: INK },
    "&:disabled": { opacity: 0.3, cursor: "not-allowed" },
  },
  headActions: { display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10 },
  headActionMeta: { fontSize: 12, color: MUTED },
  downloadBtn: {
    width: 32,
    height: 32,
    borderRadius: 7,
    border: `1px solid ${LINE}`,
    background: "#fff",
    color: INK2,
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
    "& svg": { width: 14, height: 14 },
    "&:hover": { background: SURFACE_ALT },
  },
  eraBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    height: 34,
    padding: "0 12px",
    borderRadius: 7,
    border: "1px solid transparent",
    background: ACCENT,
    color: INK,
    fontSize: 12.5,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "Inter, sans-serif",
    "& svg": { width: 14, height: 14 },
    "&:hover": { background: ACCENT_HOVER },
    "&:disabled": { opacity: 0.6, cursor: "not-allowed" },
  },

  /* ── cms paper ── */
  paper: { padding: "18px 20px", background: SURFACE_SOFT, position: "relative" },
  titleBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 12px",
    background: INK,
    color: "#fff",
    borderRadius: "6px 6px 0 0",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    "& span:last-child": { color: ACCENT },
  },
  cmsGrid: { border: `1px solid ${INK}`, borderTop: 0, background: "#fff" },
  cmsGridMt: { border: `1px solid ${INK}`, borderTop: 0, background: "#fff", marginTop: 8 },
  cmsRow: { display: "flex", borderBottom: `1px solid ${INK}`, "&:last-child": { borderBottom: 0 } },
  cmsCell: { padding: "6px 8px", borderRight: `1px solid ${INK}`, flex: 1, minWidth: 0, "&:last-child": { borderRight: 0 } },
  flex2: { flex: 2 },
  flex3: { flex: 3 },
  cmsNum: { fontSize: 9, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 2 },
  cmsVal: { fontSize: 12, color: INK, fontWeight: 500, fontFamily: "'Courier New', monospace", lineHeight: 1.4, minHeight: 16, wordBreak: "break-word" },

  /* ── lines table ── */
  linesTable: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
    marginTop: 12,
    "& th": {
      background: SURFACE_ALT,
      fontSize: 9.5,
      fontWeight: 700,
      color: MUTED,
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      padding: "6px 10px",
      borderBottom: `1px solid ${LINE_SOFT}`,
      textAlign: "left",
    },
    "& td": { padding: "9px 10px", borderBottom: `1px solid ${LINE_SOFT}`, fontSize: 12, fontFamily: "'Courier New', monospace", color: INK },
    "& tbody tr:last-child td": { borderBottom: 0 },
  },

  /* ── totals + stamp ── */
  totalsRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 24,
    padding: "14px 0 4px",
    borderTop: `1px solid ${LINE}`,
    marginTop: 12,
  },
  totalLbl: { fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: "0.06em" },
  totalVal: { fontSize: 16, fontWeight: 700, color: INK, marginTop: 2, fontFamily: "ui-monospace, monospace" },
  paidStampWrap: { position: "relative", height: 0 },
  paidStamp: {
    position: "absolute",
    right: 24,
    top: -200,
    transform: "rotate(-12deg)",
    border: `3px solid ${GREEN}`,
    padding: "4px 18px",
    borderRadius: 6,
    fontWeight: 900,
    color: GREEN,
    fontSize: 22,
    letterSpacing: 4,
    background: "rgba(255,255,255,0.85)",
    pointerEvents: "none",
    userSelect: "none",
  },
  paidStampPending: {
    position: "absolute",
    right: 24,
    top: -200,
    transform: "rotate(-12deg)",
    border: "3px solid #2C5BA3",
    padding: "4px 18px",
    borderRadius: 6,
    fontWeight: 900,
    color: "#2C5BA3",
    fontSize: 16,
    letterSpacing: 2,
    background: "rgba(255,255,255,0.85)",
    pointerEvents: "none",
    userSelect: "none",
  },
  paidStampAck: {
    position: "absolute",
    right: 24,
    top: -200,
    transform: "rotate(-12deg)",
    border: "3px solid #6941C6",
    padding: "4px 18px",
    borderRadius: 6,
    fontWeight: 900,
    color: "#6941C6",
    fontSize: 13,
    letterSpacing: 2,
    background: "rgba(255,255,255,0.85)",
    pointerEvents: "none",
    userSelect: "none",
  },
  totalValPaid:    { fontSize: 16, fontWeight: 800, color: GREEN,      marginTop: 2, fontFamily: "ui-monospace, monospace" },
  totalValPending: { fontSize: 16, fontWeight: 800, color: "#2C5BA3",  marginTop: 2, fontFamily: "ui-monospace, monospace" },
  totalValAck:     { fontSize: 16, fontWeight: 800, color: "#6941C6",  marginTop: 2, fontFamily: "ui-monospace, monospace" },

  /* ── status popup ── */
  statusPopupOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 400,
    background: "rgba(15,23,42,0.45)",
    display: "grid",
    placeItems: "center",
    padding: 24,
    zIndex: 1001
  },
  statusPopup: {
    width: "100%",
    maxWidth: 520,
    maxHeight: "84vh",
    display: "flex",
    flexDirection: "column",
    background: "#fff",
    borderRadius: 14,
    boxShadow: "0 24px 60px rgba(0,0,0,0.25)",
    overflow: "hidden",
  },
  statusPopupHead: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    padding: "18px 22px 12px",
    borderBottom: `1px solid ${LINE}`,
    "& h3": { margin: 0, fontSize: 17, fontWeight: 700, color: INK, letterSpacing: "-0.01em" },
  },
  statusPopupSub: { margin: "2px 0 0", fontSize: 12.5, color: MUTED },
  statusPopupClose: {
    width: 28,
    height: 28,
    border: 0,
    background: "transparent",
    borderRadius: 6,
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
    color: MUTED,
    flexShrink: 0,
    "& svg": { width: 14, height: 14 },
    "&:hover": { background: SURFACE_SOFT },
  },
  statusPopupBody: { padding: "18px 22px 22px", overflowY: "auto" },
  statusEvents: {
    position: "relative",
    paddingLeft: 26,
    display: "flex",
    flexDirection: "column",
    gap: 18,
    "&::before": {
      content: '""',
      position: "absolute",
      left: 9,
      top: 5,
      bottom: 5,
      width: 2,
      background: LINE,
      borderRadius: 2,
    },
  },
  statusEvent: {
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      left: -22,
      top: 4,
      width: 14,
      height: 14,
      borderRadius: 999,
      background: "#fff",
      border: `2px solid ${LINE}`,
    },
  },
  statusEventComplete: { "&::before": { background: INK, borderColor: INK } },
  statusEventSuccess:  { "&::before": { background: "#16A34A", borderColor: "#16A34A" } },
  statusEventTitle: { fontSize: 13.5, fontWeight: 700, color: INK, margin: 0, lineHeight: 1.3 },
  statusEventTitleSuccess: { color: "#15803D" },
  statusEventTitleIdle:    { color: MUTED },
  statusEventWhen: { fontSize: 11.5, color: MUTED, marginTop: 2 },
  statusEventMeta: { fontSize: 12.5, color: INK2, marginTop: 4, lineHeight: 1.45 },
  statusEventMetaIdle: { color: MUTED },

  /* ── misc ── */
  empty: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 20px", gap: 16 },
}));

/* ─── helpers ─────────────────────────────────────────────────────────────── */
const num = (v) => parseFloat(v) || 0;

const claimAmount = (c) =>
  c?.total_charge != null && c?.total_charge !== ""
    ? num(c.total_charge)
    : (c?.charge || []).reduce((t, ch) => t + num(ch?.charge), 0);

const fmtMoney = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (d) => (d ? moment(d).format("MM/DD/YYYY") : "—");

const ageFrom = (dob) => {
  if (!dob) return null;
  const a = moment().diff(moment(dob), "years");
  return Number.isFinite(a) && a >= 0 && a < 130 ? a : null;
};

const initials = (f, l) => `${(f || "")[0] || ""}${(l || "")[0] || ""}`.toUpperCase() || "?";

const clientImg = (c, allPatients) => {
  try {
    const pats = allPatients || [];
    const matched = pats.find(
      (p) =>
        p?.form1?.firstName?.toLowerCase() === (c?.pat_name_f || "").toLowerCase() &&
        p?.form1?.lastName?.toLowerCase() === (c?.pat_name_l || "").toLowerCase()
    );
    const raw = matched?.imageurl;
    if (!raw) return null;
    return decrypt(raw) || null;
  } catch {
    return null;
  }
};

const memberId = (c) => c?.ins_number || c?.pat_id || c?.payer_id || c?.subscriber_id || "N/A";

const cmsName = (c) =>
  ([c?.pat_name_l, c?.pat_name_f, c?.pat_name_m].filter(Boolean).join(", ") || "—").toUpperCase();

const patientAddress = (c) => {
  const cityLine = [c?.pat_city || c?.pat_city_name, c?.pat_state].filter(Boolean).join(", ");
  const lines = [c?.pat_addr_1, c?.pat_addr_2, [cityLine, c?.pat_zip].filter(Boolean).join(" ")].filter(Boolean);
  return lines.length ? lines : ["—"];
};

/* Safely convert a diagnosis entry (string or object) to a display string */
const diagStr = (d) => {
  if (!d) return null;
  if (typeof d === "string") return d;
  if (typeof d === "object") return d.code || d.name || d.icd || Object.values(d).find(v => typeof v === "string") || null;
  return String(d);
};

const diagnoses = (c) => {
  if (Array.isArray(c?.diagnosis) && c.diagnosis.length)
    return c.diagnosis.map(diagStr).filter(Boolean);
  const keys = ["diag1","diag2","diag3","diag4","diag_1","diag_2","diag_3","diag_4","health_diagnosis_1","health_diagnosis_2"];
  return keys.map((k) => c?.[k]).filter(Boolean);
};

const insuranceTypes = ["Medicare", "Medicaid", "Tricare", "Champva", "Group Health", "FECA", "Other"];
const insuranceLine = (c) => {
  const p = (c?.payer_name || "").toLowerCase();
  let active = "Other";
  if (p.includes("medicare")) active = "Medicare";
  else if (p.includes("medicaid")) active = "Medicaid";
  else if (p.includes("tricare")) active = "Tricare";
  else if (p.includes("champva")) active = "Champva";
  return insuranceTypes.map((t) => `${t === active ? "☑" : "☐"} ${t}`).join("  ");
};

/* ─── status events ───────────────────────────────────────────────────────── */
const buildStatusEvents = (submittedDate, paidDate, paidAmount) => [
  { state: "complete", title: "Submitted",                     when: submittedDate, meta: "Claim built locally and sent to ClaimMD clearinghouse." },
  { state: "complete", title: "Acknowledged by clearinghouse", when: submittedDate, meta: "ClaimMD accepted the 837P file (ACK 999 received)." },
  { state: "complete", title: "Forwarded to payer",            when: submittedDate, meta: "File handed off to payer for adjudication." },
  { state: "complete", title: "In review",                     when: paidDate,      meta: "Payer began adjudication on the claim line items." },
  { state: "complete", title: "Adjudicated",                   when: paidDate,      meta: "Payer completed adjudication. Full allowed amount approved." },
  { state: "success",  title: "Paid",                          when: paidDate,      meta: paidAmount ? `Payment of ${paidAmount} issued. ERA received and reconciled.` : "Payment issued. ERA received and reconciled." },
  { state: "complete", title: "Payment posted",                when: paidDate,      meta: "Posted to deposits ledger." },
];

const buildStatusEventsPending = (submittedDate) => [
  { state: "complete", title: "Submitted",                     when: submittedDate, meta: "Claim built locally and sent to ClaimMD clearinghouse." },
  { state: "complete", title: "Acknowledged by clearinghouse", when: submittedDate, meta: "ClaimMD accepted the 837P file (ACK 999 received)." },
  { state: "complete", title: "Forwarded to payer",            when: submittedDate, meta: "File handed off to payer for adjudication." },
  { state: "current",  title: "In review",                     when: "Since " + submittedDate, meta: "Payer is reviewing the claim. Average turnaround is 7–14 days." },
  { state: "idle",     title: "Adjudication",                  when: "Pending",     meta: "Awaiting the payer's final disposition." },
  { state: "idle",     title: "Outcome",                       when: "Pending",     meta: "You will be notified once a status change is received." },
];

const buildStatusEventsAcknowledged = (submittedDate) => [
  { state: "complete", title: "Submitted",                     when: submittedDate, meta: "Claim built locally and sent to ClaimMD clearinghouse." },
  { state: "complete", title: "Acknowledged by clearinghouse", when: submittedDate, meta: "ClaimMD accepted the 837P file (ACK 999 received)." },
  { state: "complete", title: "Forwarded to payer",            when: submittedDate, meta: "File handed off to payer for adjudication." },
  { state: "complete", title: "Acknowledged by payer",         when: submittedDate, meta: "Payer has received and acknowledged the claim." },
  { state: "current",  title: "Under review",                  when: "In progress",  meta: "Payer is adjudicating. Awaiting final decision." },
  { state: "idle",     title: "Outcome",                       when: "Pending",     meta: "You will be notified once a decision is reached." },
];

/* ─── component ──────────────────────────────────────────────────────────── */
const BillingPaid = () => {
  const { classes, cx } = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useSelector((state) => state?.profile);
  const { allBilling, loading } = useSelector((state) => state?.billing);
  const { patients } = useSelector((state) => state?.patient);

  const [selectedIdx, setSelectedIdx] = useState(0);
  const [eraLoading, setEraLoading] = useState(false);
  const [cmsLoading, setCmsLoading] = useState(false);
  const paperRef = useRef(null);
  const [listCollapsed, setListCollapsed] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);

  // If navigated from main table with a specific claim → show only that one
  const navClaim = location.state?.claim;
  const navBatch = location.state?.batch;
  const isNavigation = !!navClaim;

  useEffect(() => {
    if (!isNavigation) {
      getAllBillingModal({ page: "all", status: "P" });
    }
    getAllPatients("all", true, true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const paidClaims = useMemo(() => {
    // Single claim navigated from main table
    if (isNavigation) {
      return [{ claim: navClaim, batch: navBatch }];
    }
    // All paid claims from backend
    const out = [];
    (allBilling?.Billing || []).forEach((batch) => {
      (batch?.billingForm?.claim || []).forEach((claim) => {
        if (claim?.deleted === true || claim?.status !== "P") return;
        out.push({ claim, batch });
      });
    });
    return out;
  }, [allBilling, isNavigation]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedIdx > paidClaims.length - 1) setSelectedIdx(0);
  }, [paidClaims.length, selectedIdx]);

  const selected = paidClaims[selectedIdx];

  const downloadEra = async () => {
    const claimmid = selected?.claim?.claimmd_id;
    if (!claimmid) return;
    try {
      setEraLoading(true);
      const res = await GetBillingERADetais({ type: "era", claimmid });
      const eraDetails = res?.data?.billingEradetails;
      if (!eraDetails) return;
      const blob = new Blob([eraDetails.data?.[0] || ""], { type: "text/plain" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `ERA_${claimmid}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (e) {
      console.log("Error downloading ERA:", e);
    } finally {
      setEraLoading(false);
    }
  };

  const downloadCms = async () => {
    if (!paperRef.current || !selected || cmsLoading) return;
    try {
      setCmsLoading(true);
      const claimId = selected.claim?.claimmd_id || "CMS-1500";
      const html2canvas = (await import("html2canvas")).default;
      const { jsPDF } = await import("jspdf");

      const canvas = await html2canvas(paperRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
      });

      const MARGIN = 20;
      const A4_W = 595.28;
      const A4_H = 841.89;
      const printW = A4_W - MARGIN * 2;
      const printH = (canvas.height * printW) / canvas.width;

      const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });

      if (printH <= A4_H - MARGIN * 2) {
        // Fits one page
        doc.addImage(canvas.toDataURL("image/png"), "PNG", MARGIN, MARGIN, printW, printH);
      } else {
        // Multi-page: slice canvas into A4-height chunks
        const pageContentH = A4_H - MARGIN * 2;
        const totalPages = Math.ceil(printH / pageContentH);

        for (let page = 0; page < totalPages; page++) {
          if (page > 0) doc.addPage();
          const srcY = Math.round((page * pageContentH * canvas.width) / printW);
          const srcH = Math.min(
            Math.round((pageContentH * canvas.width) / printW),
            canvas.height - srcY
          );
          if (srcH <= 0) break;

          const slice = document.createElement("canvas");
          slice.width = canvas.width;
          slice.height = srcH;
          slice.getContext("2d").drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);

          const sliceH = (srcH * printW) / canvas.width;
          doc.addImage(slice.toDataURL("image/png"), "PNG", MARGIN, MARGIN, printW, sliceH);
        }
      }

      doc.save(`CMS-1500_${claimId}.pdf`);
    } catch (e) {
      console.error("CMS-1500 download error:", e);
    } finally {
      setCmsLoading(false);
    }
  };

  /* ── left list ── */
  const claimStatusLabel = (status) => {
    if (status === "P") return "Paid";
    if (status === "A") return "Acknowledged";
    return "Pending";
  };

  const renderList = () => (
    <div className={classes.list}>
      <div className={classes.listHead}>
        <h3>
          {isNavigation
            ? `1 claim`
            : `${paidClaims.length} paid claim${paidClaims.length !== 1 ? "s" : ""}`}
        </h3>
      </div>
      <div className={classes.listScroll}>
        {paidClaims.map(({ claim, batch }, i) => {
          const dateOn = batch?.createdAt ? moment(batch.createdAt).format("MMM DD") : "—";
          const statusLabel = claimStatusLabel(claim?.status);
          const cIsPaid = claim?.status === "P";
          const cIsAck  = claim?.status === "A";
          const isSelected = i === selectedIdx;
          return (
            <button
              key={`${claim?.claimmd_id || "clm"}-${i}`}
              type="button"
              className={cx(
                classes.card,
                cIsPaid  && classes.cardHoverPaid,
                !cIsPaid && !cIsAck && classes.cardHoverPending,
                cIsAck && classes.cardHoverAck,
                isSelected && cIsPaid  && classes.cardSelected,
                isSelected && !cIsPaid && !cIsAck && classes.cardSelectedPending,
                isSelected && cIsAck   && classes.cardSelectedAck,
              )}
              onClick={() => setSelectedIdx(i)}
            >
              <div className={classes.cardRow1}>
                <span className={classes.cardId}>{claim?.claimmd_id || `CLM-${i + 1}`}</span>
                <span className={classes.cardAmt}>{fmtMoney(claimAmount(claim))}</span>
              </div>
              <div className={classes.cardName}>{claim?.pat_name_f} {claim?.pat_name_l}</div>
              <div className={classes.cardPayer}>
                {claim?.payer_name || "—"} · {statusLabel.toLowerCase()} {dateOn}
                <span className={classes.cardProvider}>
                  <b>Provider:</b> {claim?.bill_name || claim?.prov_name_m || "—"}
                </span>
              </div>
              {cIsPaid && (
                <div className={classes.cardInfo}>
                  <b>ERA:</b> N/A · <b>Check #</b> N/A
                </div>
              )}
              {!cIsPaid && !cIsAck && (
                <div className={classes.cardInfoPending}>
                  <b>Stage:</b> In review with payer · awaiting adjudication
                </div>
              )}
              {cIsAck && (
                <div className={classes.cardInfoAck}>
                  <b>Stage:</b> Acknowledged by payer · awaiting adjudication
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );

  /* ── right panel ── */
  const renderForm = () => {
    if (!selected) return null;
    const { claim, batch } = selected;
    const isPaid         = claim?.status === "P";
    const isAcknowledged = claim?.status === "A";
    const img = clientImg(claim, patients?.patients);
    const age = ageFrom(claim?.pat_dob);
    const metaLine = `DOB ${fmtDate(claim?.pat_dob)}${age != null ? ` · ${age}` : ""} · Member ${memberId(claim)}`;
    const dateLabel = batch?.createdAt ? moment(batch.createdAt).format("MM/DD/YYYY") : "—";
    const submittedDate = batch?.createdAt ? moment(batch.createdAt).format("MM/DD/YYYY hh:mm A") : "—";
    const lastUpdated = batch?.updatedAt || batch?.createdAt;
    const lines = claim?.charge || [];
    const total = claimAmount(claim);
    const dx = diagnoses(claim);
    const statusLabel = claimStatusLabel(claim?.status);
    const statusEvents = isPaid
      ? buildStatusEvents(submittedDate, dateLabel, fmtMoney(total))
      : isAcknowledged
      ? buildStatusEventsAcknowledged(submittedDate)
      : buildStatusEventsPending(submittedDate);

    return (
      <div>
        {/* page id */}
        <div className={classes.pageId}>
          <h2>
            {isPaid ? "Paid Claims" : isAcknowledged ? "Acknowledged Claims" : "Pending Claims"}
            <span className={cx(
              classes.countBadge,
              isAcknowledged && classes.countBadgeAck,
              !isPaid && !isAcknowledged && classes.countBadgePending,
            )}>
              {paidClaims.length}
            </span>
          </h2>
        </div>

        {/* status trigger */}
        <button
          type="button"
          className={cx(
            classes.statusTrigger,
            isAcknowledged && classes.statusTriggerAck,
            !isPaid && !isAcknowledged && classes.statusTriggerPending,
          )}
          onClick={() => setStatusOpen(true)}
        >
          <span className={classes.statusDot} />
          <span className={classes.statusLabel}>Claim status</span>
          <span>{statusLabel} · {dateLabel}</span>
          <span style={{ opacity: 0.55, display: "inline-flex", alignItems: "center" }}>
            <LuChevronRight size={12} />
          </span>
        </button>

        {/* cms form */}
        <div className={classes.cmsForm}>
          {/* head */}
          <div className={classes.cmsFormHead}>
            <div className={classes.headPatient}>
              <div
                className={classes.avatar}
                style={img ? { backgroundImage: `url('${img}')` } : undefined}
              >
                {!img && initials(claim?.pat_name_f, claim?.pat_name_l)}
              </div>
              <div className={classes.headInfo}>
                <div className={classes.headName}>{claim?.pat_name_f} {claim?.pat_name_l}</div>
                <div className={classes.headMeta}>{metaLine}</div>
              </div>
            </div>

            <h3 className={classes.headTitle}>
              <button
                type="button"
                className={classes.navArrow}
                disabled={selectedIdx === 0}
                onClick={() => setSelectedIdx((p) => Math.max(0, p - 1))}
                aria-label="Previous paid claim"
              >
                <LuChevronLeft />
              </button>
              <span className={classes.claimIdMono}>{claim?.claimmd_id || "—"}</span>
              <span>CMS-1500 form</span>
              <button
                type="button"
                className={classes.navArrow}
                disabled={selectedIdx >= paidClaims.length - 1}
                onClick={() => setSelectedIdx((p) => Math.min(paidClaims.length - 1, p + 1))}
                aria-label="Next claim"
              >
                <LuChevronRight />
              </button>
            </h3>

            <div className={classes.headActions}>
              <span className={classes.headActionMeta}>
                Last Updated: {lastUpdated ? moment(lastUpdated).format("MM/DD/YYYY hh:mm A") : "—"}
              </span>
              <button
                type="button"
                className={classes.downloadBtn}
                title="Download CMS-1500"
                aria-label="Download CMS-1500"
                onClick={downloadCms}
                disabled={cmsLoading}
              >
                {cmsLoading ? "…" : <LuDownload />}
              </button>
              {isPaid && (
                <button
                  type="button"
                  className={classes.eraBtn}
                  onClick={downloadEra}
                  disabled={eraLoading || !claim?.claimmd_id}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
                  </svg>
                  {eraLoading ? "Loading…" : "View ERA"}
                </button>
              )}
            </div>
          </div>

          {/* paper */}
          <div className={classes.paper} ref={paperRef}>
            <div className={classes.titleBar}>
              <span>Health Insurance Claim Form · CMS-1500</span>
              <span>Pica · Pica</span>
            </div>

            <div className={classes.cmsGrid}>
              <div className={classes.cmsRow}>
                <div className={cx(classes.cmsCell, classes.flex2)}>
                  <div className={classes.cmsNum}>1. Insurance type</div>
                  <div className={classes.cmsVal}>{insuranceLine(claim)}</div>
                </div>
                <div className={classes.cmsCell}>
                  <div className={classes.cmsNum}>1a. Insured's ID number</div>
                  <div className={classes.cmsVal}>{memberId(claim)}</div>
                </div>
              </div>
              <div className={classes.cmsRow}>
                <div className={cx(classes.cmsCell, classes.flex2)}>
                  <div className={classes.cmsNum}>2. Patient's name (Last, First, MI)</div>
                  <div className={classes.cmsVal}>{cmsName(claim)}</div>
                </div>
                <div className={classes.cmsCell}>
                  <div className={classes.cmsNum}>3. Patient's birth date · Sex</div>
                  <div className={classes.cmsVal}>
                    {fmtDate(claim?.pat_dob)} &nbsp; M {claim?.pat_sex === "M" ? "☑" : "☐"} &nbsp; F {claim?.pat_sex === "F" ? "☑" : "☐"}
                  </div>
                </div>
                <div className={cx(classes.cmsCell, classes.flex2)}>
                  <div className={classes.cmsNum}>4. Insured's name (Last, First, MI)</div>
                  <div className={classes.cmsVal}>SAME</div>
                </div>
              </div>
              <div className={classes.cmsRow}>
                <div className={cx(classes.cmsCell, classes.flex3)}>
                  <div className={classes.cmsNum}>5. Patient's address</div>
                  <div className={classes.cmsVal}>
                    {patientAddress(claim).map((ln, i) => <div key={i}>{ln}</div>)}
                    {claim?.pat_phone ? <div>{claim.pat_phone}</div> : null}
                  </div>
                </div>
                <div className={classes.cmsCell}>
                  <div className={classes.cmsNum}>6. Patient relationship</div>
                  <div className={classes.cmsVal}>☑ Self ☐ Spouse ☐ Child ☐ Other</div>
                </div>
                <div className={cx(classes.cmsCell, classes.flex2)}>
                  <div className={classes.cmsNum}>7. Insured's address</div>
                  <div className={classes.cmsVal}>SAME</div>
                </div>
              </div>
              <div className={classes.cmsRow}>
                <div className={cx(classes.cmsCell, classes.flex2)}>
                  <div className={classes.cmsNum}>11. Insured's policy / group #</div>
                  <div className={classes.cmsVal}>
                    {claim?.ins_group_name || "N/A"} · {claim?.payer_name || "—"} primary
                  </div>
                </div>
                <div className={cx(classes.cmsCell, classes.flex2)}>
                  <div className={classes.cmsNum}>21. Diagnosis (ICD-10)</div>
                  <div className={classes.cmsVal}>
                    {dx.length
                      ? dx.slice(0, 12).map((d, i) => `${String.fromCharCode(65 + i)}. ${d}`).join("   ")
                      : "—"}
                  </div>
                </div>
              </div>
            </div>

            <table className={classes.linesTable}>
              <thead>
                <tr>
                  <th>24A · Date(s)</th>
                  <th>24B · POS</th>
                  <th>24D · CPT/HCPCS</th>
                  <th>24E · DX</th>
                  <th>24F · $ Charges</th>
                  <th>24G · Units</th>
                </tr>
              </thead>
              <tbody>
                {lines.length ? (
                  lines.map((ch, i) => (
                    <tr key={i}>
                      <td>
                        {ch?.from_date
                          ? `${moment(ch.from_date).format("MM/DD/YYYY")}${ch?.thru_date ? `–${moment(ch.thru_date).format("MM/DD/YYYY")}` : ""}`
                          : "—"}
                      </td>
                      <td>{ch?.pos || ch?.place_of_service || "—"}</td>
                      <td>{ch?.proc_code || ch?.procedure_code || "—"}{ch?.mod1 ? ` · ${ch.mod1}` : ""}</td>
                      <td>{ch?.diag_ref || ch?.diag || ch?.dx || "—"}</td>
                      <td>{fmtMoney(num(ch?.charge))}</td>
                      <td>{ch?.units || "—"}</td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={6}>—</td></tr>
                )}
              </tbody>
            </table>

            <div className={classes.totalsRow}>
              <div style={{ textAlign: "right" }}>
                <div className={classes.totalLbl}>28. Total charge</div>
                <div className={classes.totalVal}>{fmtMoney(total)}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div className={classes.totalLbl}>29. Amount paid</div>
                <div className={cx(
                  isPaid         ? classes.totalValPaid    :
                  isAcknowledged ? classes.totalValAck     :
                                   classes.totalValPending
                )}>
                  {isPaid ? fmtMoney(total) : "—"}
                </div>
              </div>
            </div>

            {/* Status stamp — changes based on claim status */}
            <div className={classes.paidStampWrap}>
              {isPaid         && <div className={classes.paidStamp}>PAID</div>}
              {isAcknowledged && <div className={classes.paidStampAck}>ACKNOWLEDGED</div>}
              {!isPaid && !isAcknowledged && <div className={classes.paidStampPending}>PENDING</div>}
            </div>
          </div>
        </div>

        {/* status popup */}
        {statusOpen && (
          <div
            className={classes.statusPopupOverlay}
            onClick={(e) => { if (e.target === e.currentTarget) setStatusOpen(false); }}
          >
            <div className={classes.statusPopup}>
              <div className={classes.statusPopupHead}>
                <div>
                  <h3>Claim status · {statusLabel}</h3>
                  <p className={classes.statusPopupSub}>
                    {isPaid
                      ? "Lifecycle of this claim, from submission to payment."
                      : isAcknowledged
                      ? "Lifecycle of this claim. Payer has acknowledged receipt."
                      : "Lifecycle of this claim. Awaiting adjudication."}
                  </p>
                </div>
                <button
                  type="button"
                  className={classes.statusPopupClose}
                  onClick={() => setStatusOpen(false)}
                  aria-label="Close"
                >
                  <LuX />
                </button>
              </div>
              <div className={classes.statusPopupBody}>
                <div className={classes.statusEvents}>
                  {statusEvents.map((ev, i) => (
                    <div
                      key={i}
                      className={cx(
                        classes.statusEvent,
                        ev.state === "complete" && classes.statusEventComplete,
                        ev.state === "success"  && classes.statusEventSuccess,
                      )}
                    >
                      <p className={cx(
                        classes.statusEventTitle,
                        ev.state === "success" && classes.statusEventTitleSuccess,
                        ev.state === "idle"    && classes.statusEventTitleIdle,
                      )}>
                        {ev.title}
                      </p>
                      {ev.when && <div className={classes.statusEventWhen}>{ev.when}</div>}
                      {ev.meta && (
                        <div className={cx(classes.statusEventMeta, ev.state === "idle" && classes.statusEventMetaIdle)}>
                          {ev.meta}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Box fluid className="content">
      <AppNavbar title="Billing" profile={profile} />
      <div style={{ flexDirection: "column" }} className="mainbillingcontainer">
        <ClaimToolbar />

        <button
          type="button"
          className={classes.backBtn}
          onClick={() => navigate("/home/billing")}
        >
          <LuArrowLeft />
          Back to all claims
        </button>

        {loading ? (
          <TableLoader />
        ) : paidClaims.length === 0 ? (
          <div className={classes.empty}>
            <RecordNotFound />
            <p style={{ color: "#A8A8A8", fontFamily: "Inter" }}>No paid claims found</p>
          </div>
        ) : (
          <div className={cx(classes.grid, listCollapsed && classes.gridCollapsed)}>
            {/* collapse arrow — absolutely positioned, not a grid item */}
            <button
              type="button"
              className={cx(classes.collapseArrow, listCollapsed && classes.collapseArrowCollapsed)}
              onClick={() => setListCollapsed((v) => !v)}
              aria-label={listCollapsed ? "Show claims list" : "Hide claims list"}
            >
              <span className={classes.gcIcon}><LuChevronLeft /></span>
              {listCollapsed && <span className={classes.gcLabel}>Claim List</span>}
            </button>

            {/* list — always in DOM; hidden via display:none when collapsed */}
            <div className={listCollapsed ? classes.listHidden : undefined}>
              {renderList()}
            </div>

            {renderForm()}
          </div>
        )}
      </div>
    </Box>
  );
};

export default BillingPaid;
